import { ArrowRight, CheckCircle2, LockKeyhole, Mail } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LoadingState } from '../../components/common/LoadingState';
import { getPublicAcademyBySlug, getStudentCourseSignups } from '../../firebase/firestore';
import { useAuth } from '../../hooks/useAuth';
import { getHostnameContext } from '../../utils/hostname';

export function StudentLoginPage() {
  const { login, logout, requestPasswordReset } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [academy, setAcademy] = useState(null);
  const [loadingAcademy, setLoadingAcademy] = useState(true);
  const [values, setValues] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('login');
  const [resetEmail, setResetEmail] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function loadAcademy() {
      const { tenantSlug } = getHostnameContext();
      if (!tenantSlug) {
        if (mounted) setLoadingAcademy(false);
        return;
      }
      try {
        const record = await getPublicAcademyBySlug(tenantSlug);
        if (mounted) setAcademy(record);
      } catch (loadError) {
        console.error('Unable to load academy login branding', loadError);
      } finally {
        if (mounted) setLoadingAcademy(false);
      }
    }
    loadAcademy();
    return () => { mounted = false; };
  }, []);

  async function onSubmit(event) {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      const signedInUser = await login(values);
      const { tenantSlug } = getHostnameContext();
      if (tenantSlug && academy) {
        const signups = await getStudentCourseSignups(signedInUser.uid);
        const belongsToAcademy = signups.some((signup) => signup.tenantId === academy.id);
        if (!belongsToAcademy) {
          await logout();
          setError('This student account is not connected to this academy. Use the login page for your academy.');
          return;
        }
      }
      const requestedPath = location.state?.from?.pathname || '';
      const destination = requestedPath.startsWith('/student') ? requestedPath : '/student/portal';
      navigate(destination, { replace: true });
    } catch (submitError) {
      setError(submitError.message || 'Unable to sign in. Check your email and password.');
    } finally {
      setLoading(false);
    }
  }

  async function onReset(event) {
    event.preventDefault();
    setResetError('');
    setResetLoading(true);
    try {
      await requestPasswordReset(resetEmail);
      setResetSent(true);
    } catch (resetRequestError) {
      setResetError(resetRequestError.message || 'Unable to send the reset email. Try again.');
    } finally {
      setResetLoading(false);
    }
  }

  if (loadingAcademy) return <LoadingState label="Loading academy login..." />;

  const academyName = academy?.name || 'your academy';
  const academyInitial = academy?.name?.charAt(0).toUpperCase() || 'S';
  const accent = academy?.branding?.primaryColor || '#f16b4f';
  const logoScale = Math.min(1.25, Math.max(0.75, Number(academy?.branding?.logoScale) || 1));

  return (
    <main className="academy-login-portal" style={{ '--academy-portal-accent': accent }}>
      <div className="academy-login-portal-orbit academy-login-portal-orbit--one" />
      <div className="academy-login-portal-orbit academy-login-portal-orbit--two" />
      <section className="academy-login-portal-card">
        {academy?.branding?.logoURL ? <img className="academy-login-portal-logo" src={academy.branding.logoURL} alt={`${academyName} logo`} style={{ transform: `scale(${logoScale})` }} /> : <div className="academy-login-portal-mark">{academyInitial}</div>}
        <h1>Sign in to<br /><em>your account.</em></h1>
        {view === 'login' ? (
          <>
            <p className="academy-login-portal-copy">Sign in to continue with {academyName}.</p>
            <form className="academy-login-portal-form" onSubmit={onSubmit}>
              {error ? <div className="academy-login-portal-error" role="alert">{error}</div> : null}
              <label htmlFor="student-academy-email">Email address</label>
              <div className="academy-login-portal-input"><Mail size={16} /><input id="student-academy-email" type="email" autoComplete="email" placeholder="you@example.com" required value={values.email} onChange={(event) => setValues({ ...values, email: event.target.value })} /></div>
              <label htmlFor="student-academy-password">Password</label>
              <div className="academy-login-portal-input"><LockKeyhole size={16} /><input id="student-academy-password" type="password" autoComplete="current-password" placeholder="Enter your password" required value={values.password} onChange={(event) => setValues({ ...values, password: event.target.value })} /></div>
              <button className="academy-login-portal-submit" type="submit" disabled={loading}>{loading ? 'Signing in...' : <>Sign in <ArrowRight size={16} /></>}</button>
            </form>
            <button className="academy-login-portal-reset" type="button" onClick={() => { setView('reset'); setResetEmail(values.email); setError(''); }}>Reset your password</button>
          </>
        ) : (
          <>
            <p className="academy-login-portal-copy">Enter your email address and we will send you a secure link to choose a new password.</p>
            {resetSent ? (
              <div className="academy-login-portal-reset-success"><CheckCircle2 size={19} /><strong>Reset email sent</strong><span>Check your inbox for the secure password reset link.</span></div>
            ) : (
              <form className="academy-login-portal-form" onSubmit={onReset}>
                {resetError ? <div className="academy-login-portal-error" role="alert">{resetError}</div> : null}
                <label htmlFor="student-academy-reset-email">Email address</label>
                <div className="academy-login-portal-input"><Mail size={16} /><input id="student-academy-reset-email" type="email" autoComplete="email" placeholder="you@example.com" required value={resetEmail} onChange={(event) => setResetEmail(event.target.value)} /></div>
                <button className="academy-login-portal-submit" type="submit" disabled={resetLoading}>{resetLoading ? 'Sending link...' : <>Send reset link <ArrowRight size={16} /></>}</button>
              </form>
            )}
            <button className="academy-login-portal-reset" type="button" onClick={() => { setView('login'); setResetSent(false); setResetError(''); }}>Back to sign in</button>
          </>
        )}
      </section>
    </main>
  );
}
